import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import oralExaminationImg from 'assets/icons/oral_examination_chart.png';
import { TTool } from 'types/apis/chart/studentPracticeChart';
import { TLine } from 'types/chart/pentool';
import { TOralExaminationChartFormValues } from 'types/chart/oralExaminationChart';
import useCanvas from 'hooks/chart/useCanvas';
import { Layer, Stage, Line, Image as KonvaImg } from 'react-konva';

type TProps = {
  tool: TTool;
  strokeWidth: number;
  setStrokeWidth: (strokeWidth: number) => void;
  strokeColor: string;
  strokeActive: boolean;
  lines: TLine[];
  setLines: (lines: TLine[]) => void;
  stageRef: any;
  penLayerRef: any;
  isProfessor: boolean;
  isRegister: boolean;
  isReadOnly: boolean;
};

const OralExaminationChart = ({
  tool,
  strokeWidth,
  setStrokeWidth,
  strokeColor,
  strokeActive,
  lines,
  setLines,
  stageRef,
  penLayerRef,
  isProfessor,
  isRegister,
  isReadOnly,
}: TProps) => {
  const { setValue } = useFormContext();

  const { handleTouchStart, handleTouchMove, handleTouchEnd, useImage } =
    useCanvas({
      tool,
      strokeWidth,
      strokeColor,
      lines,
      setLines: (newLines) => {
        if (!isRegister || isReadOnly) return;
        setLines(newLines);
      },
    });

  const handleTouchStartWrapper = (e: any) => {
    if (!isRegister || isReadOnly || !strokeActive) return;
    handleTouchStart(e);
  };

  const handleTouchMoveWrapper = (e: any) => {
    if (!isRegister || isReadOnly || !strokeActive) return;
    handleTouchMove(e);
  };

  const handleTouchEndWrapper = (e: any) => {
    if (!isRegister || isReadOnly || !strokeActive) return;
    handleTouchEnd(e);
  };

  const [chartImg] = useImage(oralExaminationImg);

  useEffect(() => {
    if ((isRegister && !isProfessor) || isProfessor) {
      setStrokeWidth(3);
    } else {
      setStrokeWidth(0);
    }
  }, [isRegister]);

  useEffect(() => {
    setValue(
      'OralDrawing' as keyof TOralExaminationChartFormValues,
      stageRef.current?.toJSON() as TOralExaminationChartFormValues['OralDrawing']
    );
  }, [lines]);

  return (
    <>
      <Stage
        ref={stageRef}
        width={920}
        height={500}
        className="chart_image_area"
        onMouseDown={handleTouchStartWrapper}
        onMouseMove={handleTouchMoveWrapper}
        onMouseUp={handleTouchEndWrapper}
        onMouseLeave={handleTouchEndWrapper}
        onTouchStart={handleTouchStartWrapper}
        onTouchMove={handleTouchMoveWrapper}
        onTouchEnd={handleTouchEndWrapper}
      >
        <Layer>
          {chartImg && <KonvaImg image={chartImg} width={920} height={500} />}
        </Layer>
        <Layer ref={penLayerRef}>
          {lines.map((line, i) => (
            <Line
              key={i}
              points={line.points}
              stroke={line.strokeColor}
              strokeWidth={line.strokeWidth}
              tension={0.5}
              lineCap="round"
              lineJoin="round"
              globalCompositeOperation={
                line.tool === 'eraser' ? 'destination-out' : 'source-over'
              }
            />
          ))}
        </Layer>
      </Stage>
    </>
  );
};

export default OralExaminationChart;
